import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiSelectors } from '../app/api/api.selectors';
import { IApiError, IApiRouteKey } from '../app/api/api.types';
import { useSelectWithParams } from './selector.utils';

export const useApiRequestState = (apiRouteKey: IApiRouteKey): [boolean, IApiError | null] => {
  const showLoader = useSelectWithParams(apiSelectors.selectApiInProgress, apiRouteKey);
  const error = useSelectWithParams(apiSelectors.selectApiError, apiRouteKey);

  return [showLoader, error];
};

/**
 * Generates one error message at the time.
 * Uses the message field of the response body when present.
 * Falls back to the first error in the errors object field if message is missing.
 */
export const getApiErrorMessage = (error: IApiError) => {
  if (error.message) {
    return error.message;
  }
  if (error.errors && Object.keys(error.errors).length > 0) {
    return Object.values(error.errors)[0] as string;
  }
  return '';
};

const collectErrorLines = (value: unknown, lines: string[]): void => {
  if (value == null) {
    return;
  }
  if (typeof value === 'string') {
    lines.push(value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectErrorLines(item, lines));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectErrorLines(item, lines));
    return;
  }
  lines.push(String(value));
};

export const formatApiErrorDetails = (error: Error | IApiError): string[] => {
  const lines: string[] = [];

  if (error instanceof Error) {
    if (error.message) {
      lines.push(error.message);
    }
  } else {
    collectErrorLines(error.errors, lines);
    collectErrorLines(error.error, lines);
  }

  return Array.from(new Set(lines.filter(Boolean)));
};

export const getApiFlashErrorMessage = (response: AxiosResponse<unknown>) => {
  const responseData = response.data;
  const hasApiErrorData = responseData !== null && typeof responseData === 'object';
  const apiErrorData = responseData as IApiError;

  if (response.status >= 500) {
    return {
      message: 'Server error: please try again later',
      showDetails: false,
    };
  }

  if ((response.status >= 400 && response.status < 500) || (hasApiErrorData && apiErrorData.status === 'fail')) {
    const apiMessage = hasApiErrorData ? getApiErrorMessage(apiErrorData) : undefined;

    return {
      message:
        apiMessage || (typeof responseData === 'string' ? responseData : `Error ${response.status} calling the API.`),
      showDetails: hasApiErrorData,
    };
  }

  return {
    message: `Error ${response.status} calling the API.`,
    showDetails: false,
  };
};

export const getApiReduxError = (response: AxiosResponse<unknown>): IApiError => {
  if (response.status >= 500) {
    return {
      message: 'Server error',
      statusCode: response.status,
    };
  }

  const responseData = response.data;
  if (responseData !== null && typeof responseData === 'object') {
    return {
      ...(responseData as IApiError),
      statusCode: response.status,
    };
  }

  return {
    message: typeof responseData === 'string' ? responseData : 'Something went wrong',
    statusCode: response.status,
  };
};

export const createServerErrorResponse = (): AxiosResponse<unknown> => {
  return {
    data: { message: 'Server error' },
    status: 500,
    statusText: 'Internal Server Error',
    headers: {},
    config: {} as AxiosRequestConfig,
  };
};
